/**
 * Offers — replaces services.js, packages.js, and training.js.
 * Three flagship offers, plus a free-audit front door.
 * Named exports + grouped offers object.
 *
 * Copy refreshed 2026-06-09 (analysis-first repositioning):
 * The Foundry -> The Forge, Fully Automated Business Systems -> BOS AI,
 * Enterprise AI Training -> Team and Enterprise Training. Free-audit front
 * door added as `auditBand`. Pricing removed per directive (no figures
 * anywhere). Export names, ids, and spokeHrefs are intentionally unchanged
 * to avoid touching routing/imports.
 */

export const offersHead = {
  eyebrow: 'Work with us',
  heading: 'Three ways to work with us.',
  description:
    'It comes down to how much you want to own: the fix and the keys, the whole operation with you supervising, or your own people trained to build it themselves.',
};

// Free-audit front door — sits above the three offers. CTA opens Merlin.
export const auditBand = {
  eyebrow: 'Start here',
  heading: 'We find your highest-ROI opportunities first. For free.',
  body:
    'We walk your operation function by function to find where time and money leak, and where you are safe or exposed on AI. You get a ranked report of your highest-return opportunities, free and with no obligation. Take it and run it yourself, or have us build it.',
  cta: { label: 'Get your free audit', href: '#cta' },
};

export const foundry = {
  id: 'foundry',
  title: 'The Forge',
  subtitle: 'Custom builds you own',
  description:
    'You have already named the problem. The Forge is where we build the system that ends it: custom software, automations, migrations, integrations, AI where it earns its place and plain engineering where it does not. A senior engineer scopes the hard part and builds it into the tools you already run, your team is trained to operate it, and the code lands in your repo with no subscription to us. One Perth practice came to us for an integration their vendor said was impossible. We shipped it, handed them the code, and that build is now the operating system the firm runs on.',
  features: [
    'A senior engineer on the build start to finish, on the part SaaS, no-code, and DIY all walk away from',
    'The code in your repo, the data on your infrastructure: you own it, no lock-in, take it to any engineer alive',
    'Built into the tools you already run, so what you capture once flows everywhere it needs to',
    'Scoped tight, not an open hourly meter, with your team trained to run it. Weeks, not quarters',
  ],
  cta: { label: 'Bring Merlin your problem', href: '#cta' },
  meta: { timeline: 'Weeks, not quarters', model: 'Fixed scope, you own it' },
  spokeHref: '/foundry',
};

export const automatedSystems = {
  id: 'automated-systems',
  title: 'BOS AI',
  subtitle: 'The Business Operating System',
  description:
    'BOS AI is an operating system that runs on top of your whole business. It learns how your business actually works, your goals, KPIs, pipelines, brand, and live data from the tools you already use. It helps you set the goals, tracks the KPIs that feed them, and tells you what is worth doing next to hit them. Then it acts. The connections into your stack and the specialised systems that do the work do not come out of a box. We build those onto your business one at a time, and once they are in, the system runs and measures them. Running today inside a Perth accounting practice, our first business on the system.',
  features: [
    'Learns your whole business across every function, your goals, KPIs, pipelines, and brand, so every agent works from your reality',
    'Helps you set goals, tracks the KPIs that feed them, and ranks what is worth doing next to hit them',
    'Then it acts: we build the connections and specialised systems that do the work onto your business, one at a time',
    'Instrumented from day one: every job, every run, and every dollar of spend logged and visible, on infrastructure you own. No black box',
  ],
  cta: { label: 'See if it fits your business', href: '#cta' },
  meta: { timeline: 'Built piece by piece', model: 'Runs on your infrastructure' },
  spokeHref: '/automated-systems',
};

export const teamTraining = {
  id: 'team-training',
  title: 'Team and Enterprise Training',
  subtitle: 'Your people, building their own',
  description:
    'One session that ends with your team having built working agents inside their own jobs, not sitting through a lecture about AI. We cover governance and data safety for a real compliance context, how AI actually works, and the wiring that connects an agent to the tools you already run. Hands-on across Claude, ChatGPT for Business, and Gemini. We teach the patterns we run in production, not theory off a slide.',
  features: [
    'Governance and data safety first, taught for a compliance context: what is safe in which tool, where the line sits for client data',
    'How AI actually works: what it is good and bad at, how to instruct it, how to spot output that sounds right and is wrong',
    'Context, MCPs, and connectors: wiring an agent into the Xero, Slack, or Drive your team already uses',
    'Each person maps their own role and builds one working agent inside it. On-site or virtual, repeatable for the next team',
  ],
  cta: { label: 'Scope a session with Merlin', href: '#cta' },
  meta: { timeline: 'One session', model: 'On-site or virtual' },
  spokeHref: '/team-training',
};

export const offers = {
  flagship: [foundry, automatedSystems, teamTraining],
};
